// Dojos the game promotes. Only locations with adult classes and a free
// trial lesson (checked on each dojo's own site, 2026-09-26; Kremmen
// 2026-09-30), and kids' classes with a free trial too (2026-10-17): the game
// speaks to adults training for themselves and to kids, whose parents book.
// Names and addresses are proper nouns and are not translated.

/** The room the fight is drawn in (dojo-stage exams): each partner dojo gets
 *  its own look, inspired by the place rather than a copy of it. */
export type DojoStyle = 'kamiza' | 'hall' | 'mural' | 'frost' | 'station';

export interface Dojo {
  id: string;
  name: string;
  area: string;
  address: string;
  /** Where "Book your free trial" goes: the dojo's trial / contact page. */
  trialUrl: string;
  /** Where "Free trial for kids" goes: the page with this venue's kids' classes. */
  kidsUrl: string;
  style: DojoStyle;
  /** Where it is, for "nearest to me" (WGS84 degrees). */
  lat: number;
  lon: number;
}

export const DOJOS: readonly Dojo[] = [
  {
    id: 'sk-schoeneberg',
    name: 'Samurai Karate Berlin',
    area: 'Schöneberg',
    address: 'Wilhelm-Kabus-Str. 21–35, 10829 Berlin',
    trialUrl: 'https://samurai-karate.berlin/kontakt/',
    kidsUrl: 'https://samurai-karate.berlin/kinderkarate/',
    style: 'kamiza',
    lat: 52.4796,
    lon: 13.3547,
  },
  {
    id: 'sk-kreuzberg',
    name: 'Samurai Karate Kreuzberg',
    area: 'Kreuzberg',
    address: 'FitnessCenter aTB, Am Tempelhofer Berg 6, 10965 Berlin',
    trialUrl: 'https://www.karate-kreuzberg.de/kontakt.html',
    kidsUrl: 'https://www.karate-kreuzberg.de/kinderkarate.html',
    style: 'hall',
    lat: 52.4857,
    lon: 13.3858,
  },
  {
    id: 'ki-pankow',
    name: 'Ki Dojo Pankow',
    area: 'Pankow',
    address: 'Hauptstr. 21, 13127 Berlin',
    trialUrl: 'https://www.ki-dojo.de/pankow-buchholz.html',
    // The venue's page: its timetable lists the kids' classes too
    kidsUrl: 'https://www.ki-dojo.de/pankow-buchholz.html',
    style: 'mural',
    lat: 52.6006,
    lon: 13.4291,
  },
  {
    id: 'sk-frohnau',
    name: 'Samurai Karate Frohnau',
    area: 'Frohnau / Glienicke',
    address: 'Therapiezentrum, Leipziger Str. 1, 16548 Glienicke',
    trialUrl: 'https://www.karate-frohnau.de/kontakt.html',
    kidsUrl: 'https://www.karate-frohnau.de/kinderkarate.html',
    style: 'frost',
    lat: 52.6343,
    lon: 13.3188,
  },
  {
    id: 'ki-kremmen',
    name: 'Ki Dojo Kremmen',
    area: 'Kremmen',
    address: 'Bahnhof Kremmen, Berliner Chaussee 13, 16766 Kremmen',
    trialUrl: 'https://www.ki-dojo.de/kremmen.html',
    // The venue's page: its timetable lists the kids' classes too
    kidsUrl: 'https://www.ki-dojo.de/kremmen.html',
    style: 'station',
    lat: 52.7537,
    lon: 13.0393,
  },
];

export function dojoById(id: string | null | undefined): Dojo | undefined {
  return DOJOS.find((d) => d.id === id);
}

/** Where on the site a trial link was shown — becomes utm_medium. */
export type Placement =
  'promotion' | 'fail' | 'daily' | 'kumite' | 'dojo-board' | 'picker' | 'title';

/**
 * The dojo's trial page (or its kids' classes), tagged so each dojo can see
 * the visit came from the game (and from which moment in it) in their own
 * analytics.
 */
export function trialLink(
  dojo: Dojo,
  placement: Placement,
  beltId: string,
  picked = true,
  kids = false,
): string {
  const url = new URL(kids ? dojo.kidsUrl : dojo.trialUrl);
  url.searchParams.set('utm_source', 'kiai-game');
  url.searchParams.set('utm_medium', placement);
  url.searchParams.set('utm_campaign', dojo.id);
  url.searchParams.set('utm_content', beltId);
  // For a child, and drawn at random rather than picked by the player
  const term = [kids && 'kids', !picked && 'preselected'].filter(Boolean).join('-');
  if (term) url.searchParams.set('utm_term', term);
  return url.toString();
}

/**
 * Until the player picks a dojo: one drawn at random for them, so each
 * partner dojo gets its share of first impressions.
 */
export function randomDojo(rand: () => number = Math.random): Dojo {
  return DOJOS[Math.min(DOJOS.length - 1, Math.floor(rand() * DOJOS.length))]!;
}

/** Great-circle distance in km, for "nearest to me". */
export function distanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad;
  const dLon = (lon2 - lon1) * rad;
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.min(1, Math.sqrt(a)));
}
